import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { resolveCanUseSelfService } from '../dashboard/resolveDashboardGates';
import {
  YEAR_WINDOW, todayInIstanbul, buildDayIndex, yearTotals,
} from './calendarModel';
import { fmtLongDate, weekdayLong } from './calendarFormat';
import useMyCalendar from './useMyCalendar';
import MonthGrid from './MonthGrid';
import CalendarLegend from './CalendarLegend';
import DayHoverTip from './DayHoverTip';
import DayDetailCard from './DayDetailCard';

const ALL_LAYERS = ['holiday', 'approved', 'waiting'];
const MONTHS = Array.from({ length: 12 }, (_, m) => m);

const canHover = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && !!window.matchMedia('(hover: hover)')?.matches;

/**
 * Takvimim (spec 2026-10-30) — giriş yapan kişinin yılı tek ekranda: resmî tatiller, onaylı ve bekleyen izinler.
 * Sayfa gövdesinde başlık YOK (UI altın kuralı); tek yüzey kartı. İzin katmanları "İzinlerim" menüsünün kapısıyla
 * (LM aboneliği + koltuk) gösterilir; kapı kapalıysa sayfa yalnız tatillerle çalışır.
 */
export default function MyCalendarPage() {
  const { t } = useTranslation();
  const { hasModule, hasSeat } = useAuth();
  const showLeaves = resolveCanUseSelfService('LM', { hasModule, hasSeat });

  const today = useMemo(() => todayInIstanbul(), []);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const { data, error } = useMyCalendar(year);

  const [layerState, setLayerState] = useState({ holiday: true, approved: true, waiting: true });
  const visible = useMemo(
    () => (showLeaves ? layerState : { ...layerState, approved: false, waiting: false }),
    [showLeaves, layerState],
  );
  const [focusLayer, setFocusLayer] = useState(null);
  const [hover, setHover] = useState(null);
  const [picked, setPicked] = useState(null);
  const [flashTick, setFlashTick] = useState(0);
  const gridRef = useRef(null);
  const pickedRef = useRef(null);
  pickedRef.current = picked;
  const hoverEnabled = useMemo(canHover, []);

  const index = useMemo(() => buildDayIndex(data), [data]);
  const totals = useMemo(() => yearTotals(data, year), [data, year]);
  const layers = showLeaves ? ALL_LAYERS : ['holiday'];
  const calendars = data?.holidayCalendars || [];
  const currentMonth = year === thisYear ? Number(today.slice(5, 7)) - 1 : -1;

  const closeCard = useCallback(() => setPicked(null), []);

  const changeYear = (next) => {
    setPicked(null);
    setHover(null);
    setYear(next);
  };

  const toggleLayer = (k) => {
    setPicked(null);
    setHover(null);
    setFocusLayer(null);
    setLayerState((s) => ({ ...s, [k]: !s[k] }));
  };

  const pendingFlash = useRef(false);
  const goToday = () => {
    pendingFlash.current = true;
    changeYear(thisYear);
    setFlashTick((n) => n + 1);
  };

  // "Bugün": bugünü görünür alana kaydır, halkayı kısa süre nabız gibi vurgula — veri gelince, TEK kez (bekleyen istek
  // bayrağı tüketilir; oklarla bu yıla dönmek tekrar vurgulamaz).
  useEffect(() => {
    if (!pendingFlash.current || year !== thisYear || !data) return;
    const el = gridRef.current?.querySelector(`[data-date="${today}"]`);
    if (!el) return;
    pendingFlash.current = false;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('flash');
    el.getBoundingClientRect(); // yeniden akış: animasyon baştan başlasın
    el.classList.add('flash');
  }, [flashTick, data, year, thisYear, today]);

  const onDayEnter = useCallback((day, el) => {
    if (!hoverEnabled || pickedRef.current) return;
    setHover({ day, anchor: el });
  }, [hoverEnabled]);

  const onDayActivate = useCallback((day, el) => {
    setHover(null);
    setPicked({ day, anchor: el });
  }, []);

  const dayLabel = useCallback((c) => {
    const names = [
      ...(c.cell.holiday ? [c.cell.holiday.name] : []),
      ...c.cell.leaves.map((l) => `${l.leaveVariationName} (${t(`myCalendar.tag.${l.status}`)})`),
    ];
    return `${fmtLongDate(t, c.date)}, ${weekdayLong(t, c.date)}: ${names.join(', ')}`;
  }, [t]);

  return (
    <Surface>
      <Toolbar>
        <YearNav>
          <Tooltip text={t('myCalendar.prevYear')}>
            <IconButton
              size="sm"
              aria-label={t('myCalendar.prevYear')}
              disabled={year <= thisYear - YEAR_WINDOW}
              onClick={() => changeYear(year - 1)}
              data-testid="calendar-prev-year"
            >
              <ChevronLeft size={16} />
            </IconButton>
          </Tooltip>
          <Year aria-live="polite" data-testid="calendar-year">{year}</Year>
          <Tooltip text={t('myCalendar.nextYear')}>
            <IconButton
              size="sm"
              aria-label={t('myCalendar.nextYear')}
              disabled={year >= thisYear + YEAR_WINDOW}
              onClick={() => changeYear(year + 1)}
              data-testid="calendar-next-year"
            >
              <ChevronRight size={16} />
            </IconButton>
          </Tooltip>
          <TodayButton variant="outline" onClick={goToday}>{t('myCalendar.today')}</TodayButton>
          {calendars.length === 1 && (
            <CalendarSource>{t('myCalendar.calendarSource', { name: calendars[0].name })}</CalendarSource>
          )}
        </YearNav>
        <CalendarLegend
          layers={layers}
          visible={visible}
          totals={totals}
          onToggle={toggleLayer}
          onFocusLayer={setFocusLayer}
        />
      </Toolbar>

      {error && <LoadErrorBanner message={t('myCalendar.loadError')} />}

      <MonthsWrap
        ref={gridRef}
        className={focusLayer ? `focus-${focusLayer}` : undefined}
        onMouseLeave={() => setHover(null)}
      >
        <Months>
          {MONTHS.map((m) => (
            <MonthGrid
              key={m}
              year={year}
              monthIndex={m}
              index={index}
              visible={visible}
              today={today}
              isCurrentMonth={m === currentMonth}
              pickedDate={picked?.day.date || null}
              onDayEnter={onDayEnter}
              onDayActivate={onDayActivate}
              dayLabel={dayLabel}
            />
          ))}
        </Months>
      </MonthsWrap>

      {hover && !picked && (
        <DayHoverTip
          day={hover.day}
          anchor={hover.anchor}
          entry={index.get(hover.day.date)}
          index={index}
          today={today}
        />
      )}
      {picked && (
        <DayDetailCard
          key={picked.day.date}
          day={picked.day}
          anchor={picked.anchor}
          index={index}
          holidayCalendars={calendars}
          showLeaves={showLeaves}
          onClose={closeCard}
        />
      )}
    </Surface>
  );
}

const Surface = styled.section`
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.04), 0 8px 24px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.05);
  padding: 20px 24px 24px;

  @media (max-width: 600px) {
    padding: 14px 12px 16px;
    border-radius: 14px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;

  @media (max-width: 600px) { gap: 12px; }
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Year = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.3px;
  min-width: 70px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TodayButton = styled(Button)`
  padding: 6px 14px;
  margin-left: 6px;
`;

const CalendarSource = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.calendar.faint};
  margin-left: 10px;

  @media (max-width: 600px) { display: none; }
`;

// Yılın tamamı tek bakışta: kap ≥ ~1000 px'te 6 × 2; daralınca 4, 3, 2 sütun (container query).
const MonthsWrap = styled.div`
  container-type: inline-size;

  &.focus-holiday .day:not(.k-holiday) .band,
  &.focus-holiday .day:not(.k-holiday):not(.empty) .num,
  &.focus-approved .day:not(.k-approved) .band,
  &.focus-approved .day:not(.k-approved):not(.has-papproved) .num,
  &.focus-waiting .day:not(.k-waiting) .band,
  &.focus-waiting .day:not(.k-waiting):not(.has-pwaiting) .num { opacity: 0.25; }
`;

const Months = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px 14px;

  @container (max-width: 990px) { grid-template-columns: repeat(4, 1fr); gap: 20px 22px; }
  @container (max-width: 700px) { grid-template-columns: repeat(3, 1fr); }
  @container (max-width: 520px) { grid-template-columns: repeat(2, 1fr); gap: 14px 10px; }
  @container (max-width: 300px) { grid-template-columns: 1fr; }
`;
